import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import { BModal, BvModalEvent, VBTooltip, BPagination } from 'bootstrap-vue'
import { Spinner } from '@kwixeo/interface-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { KFormFile } from '@kwixeo/form-vue'
import { mapGetters } from 'vuex'

@Component({
  components: {
    Spinner,
    FontAwesomeIcon,
    KFormFile,
    BModal,
    BPagination
  },
  computed: {
    ...mapGetters('user', ['can'])
  },
  directives: {
    'b-tooltip': VBTooltip
  }
})
export default class CatalogPictures extends Vue {
  can!: (key: string, level: number) => boolean

  @Prop({ required: true }) id!: number
  @Prop({ required: true }) type!: string
  @Prop({ required: true }) defaultPicture!: number
  @Prop({ default: 150 }) size!: number
  @Prop({ default: false }) withSelection!: false|((picture: KwixeoCatalog.Picture) => void)

  protected pictures: KwixeoCatalog.Pictures = []
  protected preview: KwixeoCatalog.Picture|null = null
  private loading = false
  private toDelete: number|null = null
  private deleting = false
  private updating: number|null = null
  
  private perPage = 20
  private page = 1
  private total = 0

  get apiUrl (): string {
    return '/catalog/' + this.type + '/' + this.id + '/picture'
  }

  get defaultIcon (): (img: KwixeoCatalog.Picture) => string[] {
    return (img: KwixeoCatalog.Picture): string[] => {
      if (this.updating === img.id) {
        return ['fad', 'spinner-third']
      } else if (this.defaultPicture === img.id) {
        return ['fas', 'star']
      } else {
        return ['fal', 'star']
      }
    }
  }

  async created (): Promise<void> {
    await this.loadPictures()
  }

  @Watch('page')
  async loadPictures (): Promise<void> {
    this.loading = true
    await this.$kwCatalog.axios.get(this.apiUrl, {
        params: {
          per_page: this.perPage,
          page: this.page,
          with_file: true
        }
      })
      .then(response => {
        this.pictures = response.data.data
        this.total = response.data.meta.total
        this.loading = false
      })
      .catch(error => {
        throw new Error(error)
      })
  }

  @Emit('total')
  @Watch('total')
  watchPictures (): number {
    return this.total
  }

  private uploaded (response: KwixeoCatalog.Picture): void {
    this.pictures.unshift(response)
    this.total++
  }

  private downloadPicture (img: KwixeoCatalog.Picture): void {
    const a = document.createElement('a')
    a.href = 'data:image/png;base64,' + img.file
    a.download = img.filename
    a.click()
  }

  private async deletePicture (event: BvModalEvent): Promise<void> {
    const idx = this.toDelete
    if (idx === null) {
      return
    }

    event.preventDefault()
    this.deleting = true

    const img: KwixeoCatalog.Picture = this.pictures[idx]
    await this.$kwCatalog.axios.delete(this.apiUrl + '/' + img.id, {
      params: {
        with_file: true
      }
    })
      .then(() => {
        Vue.delete(this.pictures, idx)
        this.total--
      })
      .catch(error => {
        throw new Error(error)
      })
      .finally(() => {
        this.deleting = false
        this.toDelete = null
      })
  }

  private async changeDefault (id: number): Promise<void> {
    if (this.updating !== null || !this.can('catalog-products', 3)) {
      return
    }
    this.updating = id
    await this.$kwCatalog.axios.put('/catalog/' + this.type + '/' + this.id, {
        picture: id
      })
      .then(response => {
        this.$emit('defaultPicture', id)
        this.$emit('productUpdated', response.data)
      })
      .catch(error => {
        throw new Error(error)
      })
      .finally(() => {
        this.updating = null
      })
  }
}
